import { InteractionStatus } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

import axios from '@lib/axios'
import { userInfo } from '@lib/microsoft-authentication-library'
import { createToast } from '@lib/toast'
import { captureError } from 'capture-error'
import { LoggedInUser } from './user-context-types'

const UserContext = createContext<{ user: LoggedInUser; isAuthenticated: boolean; inProgress: InteractionStatus }>({
  user: null,
  isAuthenticated: false,
  inProgress: InteractionStatus.None,
})

export function UserContextProvider({ children }) {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser>(null)

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const msalUserInfo = await userInfo()
        const agent = await axios.get(`/agents/${msalUserInfo.email}`)
        const user: LoggedInUser = {
          displayName: msalUserInfo.displayName,
          email: msalUserInfo.email,
          agentCode: agent.agentId,
        }

        setLoggedInUser(user)
      } catch (error) {
        createToast(error.message, 'error')

        captureError({ error })
      }
    }

    if (isAuthenticated && inProgress === InteractionStatus.None) {
      loadUserInfo()
    }
  }, [inProgress, instance, isAuthenticated])

  const contextValue = useMemo(
    () => ({
      user: loggedInUser,
      isAuthenticated,
      inProgress,
    }),
    [loggedInUser, inProgress, isAuthenticated],
  )

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
}

export const useUserContext = () => {
  return useContext(UserContext)
}
